import { type RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import ChevronLine from '@/assets/icons/chevron_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { CopyAsImageButton } from '@/components/molecules/CopyAsImageButton';
import DateRangePicker from '@/components/molecules/DateRangePicker/DateRangePicker';
import { DATE_RANGE_PRESETS } from '@/components/molecules/DateRangePicker/DateRangePresets';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { ExportDataDropDown } from '@/components/molecules/ExportDataDropDown/ExportDataDropDown';
import type { HealthOverviewParameter, HealthWoundTrendPoint } from '@/domain/health';
import { HEALTH_DEFAULT_DATE_RANGE_PRESET_ID } from '@/routes/Client/Cage/pages/Health/components/healthDetailChartConstants';
import LaksvelScoreLegend from '@/routes/Client/Cage/pages/Health/components/LaksvelScoreLegend/LaksvelScoreLegend';
import { useHealthOverviewDateRange } from '@/routes/Client/Cage/pages/Health/context/HealthOverviewDateRangeContext';
import { COPY_EXCLUDE_ATTR } from '@/utils/elementCapture';

import {
    buildHealthOverviewDownloadData,
    createHealthOverviewExportLabelContext,
} from '../../healthOverviewDownloadData';

import styles from './LaksvelOverviewChart.module.scss';

/** Default window ending today. Read at check time so midnight does not keep yesterday. */
function defaultHealthOverviewRange() {
    return DATE_RANGE_PRESETS.find(
        (preset) => preset.id === HEALTH_DEFAULT_DATE_RANGE_PRESET_ID
    )?.calculateDates();
}

interface OverviewSectionHeaderProps {
    parameters: HealthOverviewParameter[];
    woundDailyTrend: HealthWoundTrendPoint[];
    getLabel: (key: string) => string;
    captureRef: RefObject<HTMLElement | null>;
    isExportDisabled?: boolean;
    allExpanded: boolean;
    onToggleAll: () => void;
    isDateRangeDisabled?: boolean;
}

export default function OverviewSectionHeader(props: OverviewSectionHeaderProps) {
    const {
        parameters,
        woundDailyTrend,
        getLabel,
        captureRef,
        isExportDisabled = false,
        allExpanded,
        onToggleAll,
        isDateRangeDisabled = false,
    } = props;
    const { t } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();
    const overviewDateRange = useHealthOverviewDateRange();
    const todayTo = defaultHealthOverviewRange()?.dateTo;
    const showingEarlierDay =
        overviewDateRange?.dateTo != null &&
        todayTo != null &&
        overviewDateRange.dateTo !== todayTo;

    function returnToToday() {
        const todayRange = defaultHealthOverviewRange();
        if (todayRange?.dateFrom == null || todayRange.dateTo == null) {
            return;
        }
        overviewDateRange?.onDateChange({
            dateFrom: todayRange.dateFrom,
            dateTo: todayRange.dateTo,
        });
    }

    const exportPayload = useMemo(() => {
        if (isExportDisabled || parameters.length === 0) {
            return null;
        }

        const labels = createHealthOverviewExportLabelContext(t, tGlobal, getLabel);
        return buildHealthOverviewDownloadData(parameters, woundDailyTrend, labels);
    }, [parameters, woundDailyTrend, getLabel, isExportDisabled, t, tGlobal]);

    return (
        <div className={styles.sectionHeader}>
            <div className={styles.sectionHeaderRow}>
                <div className={styles.sectionHeadingGroup}>
                    <h2 className={styles.sectionHeading}>{t('dashboard.overview')}</h2>
                    <span {...{ [COPY_EXCLUDE_ATTR]: '' }}>
                        <Button
                            variant="tertiary"
                            size="small"
                            type="button"
                            disabled={parameters.length === 0}
                            trackingId="health_overview_toggle_all"
                            aria-expanded={allExpanded}
                            onClick={onToggleAll}
                            iconEnd={
                                <SvgIcon
                                    icon={ChevronLine}
                                    size="small"
                                    color="currentColor"
                                    className={classNames(
                                        styles.toggleAllChevron,
                                        allExpanded && styles.toggleAllChevronOpen
                                    )}
                                    svgProps={{ 'aria-hidden': true }}
                                />
                            }>
                            {allExpanded ? t('dashboard.collapseAll') : t('dashboard.expandAll')}
                        </Button>
                    </span>
                </div>
                <div className={styles.sectionHeaderActions} {...{ [COPY_EXCLUDE_ATTR]: '' }}>
                    {showingEarlierDay ? (
                        <Button
                            variant="tertiary"
                            size="small"
                            type="button"
                            trackingId="health_overview_back_to_today"
                            onClick={returnToToday}>
                            {t('dashboard.backToToday')}
                        </Button>
                    ) : null}
                    <DateRangePicker
                        disabled={isDateRangeDisabled || overviewDateRange == null}
                        menuPosition="right"
                        defaultPresetId={HEALTH_DEFAULT_DATE_RANGE_PRESET_ID}
                        dateFrom={overviewDateRange?.dateFrom}
                        dateTo={overviewDateRange?.dateTo}
                        onDateChange={overviewDateRange?.onDateChange}
                    />
                    {exportPayload ? (
                        <ExportDataDropDown>
                            <DownloadOptions
                                data={exportPayload.data}
                                columns={exportPayload.columns}
                                nameToInclude="HealthOverview">
                                <CopyAsImageButton
                                    targetRef={captureRef}
                                    exportType="HealthOverview"
                                />
                            </DownloadOptions>
                        </ExportDataDropDown>
                    ) : null}
                </div>
            </div>
            <LaksvelScoreLegend className={styles.scoreLegend} />
        </div>
    );
}
